import { useTranslation } from '@/lib/i18n';
import type {
  PayoutTransaction,
  Settlement,
} from '@/lib/api-admin/types';

export interface AuditTrailPanelProps {
  settlement: Settlement;
  payouts: ReadonlyArray<PayoutTransaction>;
}

interface AuditEntry {
  id: string;
  label: string;
}

const formatTime = (iso: string | null | undefined): string => {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString('ko-KR');
};

export const AuditTrailPanel = ({ settlement, payouts }: AuditTrailPanelProps) => {
  const { t } = useTranslation('settlement');

  const entries: AuditEntry[] = [];

  if (settlement.approvedByAgentId) {
    entries.push({
      id: `agent-${settlement.runId}`,
      label: t('settlement.audit.agentLine', {
        admin: settlement.approvedByAgentId,
        at: formatTime(settlement.computedAt),
      }),
    });
  }
  if (settlement.approvedBySuperAdminId) {
    entries.push({
      id: `super-${settlement.runId}`,
      label: t('settlement.audit.superLine', {
        admin: settlement.approvedBySuperAdminId,
        at: formatTime(settlement.updatedAt),
      }),
    });
  }
  for (const tx of payouts) {
    entries.push({
      id: `payout-${tx.payoutTxId}`,
      label: t('settlement.audit.payoutLine', {
        admin: tx.vendorId,
        at: formatTime(tx.initiatedAt ?? tx.createdAt),
      }),
    });
  }

  return (
    <section
      aria-labelledby="audit-trail-heading"
      data-testid="audit-trail-panel"
      className="rounded-lg p-5"
      style={{ background: 'var(--surface-raised)', boxShadow: 'var(--shadow-card)' }}
    >
      <h3
        id="audit-trail-heading"
        className="mb-3 text-sm font-semibold text-[color:var(--content-strong)]"
      >
        {t('settlement.audit.title')}
      </h3>
      {entries.length === 0 ? (
        <p className="text-sm text-[color:var(--content-muted)]">
          {t('settlement.audit.empty')}
        </p>
      ) : (
        <ol className="flex flex-col gap-2">
          {entries.map((entry) => (
            <li
              key={entry.id}
              className="flex items-start gap-2 rounded-md p-2.5 text-xs"
              style={{ background: 'var(--surface-1)' }}
              data-testid="audit-entry"
            >
              <span
                aria-hidden="true"
                className="mt-0.5 inline-block h-2 w-2 rounded-full"
                style={{ background: 'var(--cta-primary)' }}
              />
              <span className="text-[color:var(--content-default)]">{entry.label}</span>
            </li>
          ))}
        </ol>
      )}
    </section>
  );
};
